import React, { useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar, TextField, Typography } from '@mui/material';
import { BuffOrDebuff, MemberRole, TreasureItem, WarbandMemberDTO } from '../types';
import { getBuffName } from '../utils/formatting';
import { getSplitStatValue } from '../utils/splitStats';
import { useDirtyTrackingStore } from '../stores/dirtyTrackingStore';
import { useReferenceDataStore } from '../stores/referenceDataStore';
import { ItemDetailsPopover } from '../components/shared/ItemDetailsPopover';
import { ConfirmActionDialog } from '../components/shared/ConfirmActionDialog';
import { UseScrollModal } from '../components/modals/UseScrollModal';
import {
  clearItemRemainingUses,
  getItemRemainingUses,
  setItemRemainingUses,
} from '../services/localStorageService';

interface UseTreasureItemActionsInput {
  member: WarbandMemberDTO;
  memberRole: MemberRole;
  warbandBuffs: BuffOrDebuff[];
}

/**
 * Play-mode treasure item popover with a Use action: applies linked buffs
 * (member, warband-wide, or opponent notice), heals, casts scrolls, tracks
 * per-game uses, and removes consumed items.
 */
export const useTreasureItemActions = ({ member, memberRole, warbandBuffs }: UseTreasureItemActionsInput) => {
  const { warbandId, id: memberId } = member;
  const allBuffs = useReferenceDataStore((state) => state.buffs);
  const updateMemberBuffs = useDirtyTrackingStore((s) => s.updateMemberBuffs);
  const updateWarbandBuffs = useDirtyTrackingStore((s) => s.updateWarbandBuffs);
  const updateMemberTreasureItems = useDirtyTrackingStore((s) => s.updateMemberTreasureItems);
  const updateMemberHealth = useDirtyTrackingStore((s) => s.updateMemberHealth);
  const toggleMemberPoisoned = useDirtyTrackingStore((s) => s.toggleMemberPoisoned);

  const [treasureItemPopover, setTreasureItemPopover] = useState<{ anchor: HTMLElement; item: TreasureItem } | null>(null);
  const [pendingUseItem, setPendingUseItem] = useState<TreasureItem | null>(null);
  const [healInput, setHealInput] = useState(0);
  const [opponentNotice, setOpponentNotice] = useState<string | null>(null);

  const maxHealth = getSplitStatValue(member, 'health', member.health, warbandBuffs);

  React.useEffect(() => {
    if (pendingUseItem?.healAmount != null) {
      setHealInput(pendingUseItem.healAmount === 'full'
        ? Math.max(0, maxHealth - member.currentHealth)
        : pendingUseItem.healAmount);
    }
  }, [pendingUseItem, maxHealth, member.currentHealth]);

  const getApplicableBuff = (item: TreasureItem): BuffOrDebuff | null => {
    if (item.buffId) return allBuffs.find((b) => b.id === item.buffId) ?? null;
    if (item.invokedSpellId) return allBuffs.find((b) => b.sourceId === item.invokedSpellId) ?? null;
    return null;
  };

  const getUseItemMessage = (): string => {
    if (!pendingUseItem) return '';
    const buff = getApplicableBuff(pendingUseItem);
    if (!buff) return 'No automated effect — apply the effect manually at the table.';
    const buffName = getBuffName(buff);
    const isGlobalYou = buff.globalYou > 0;
    const isGlobalThem = buff.globalThem > 0;
    if (isGlobalYou && isGlobalThem) {
      return `This will apply ${buffName} to your warband. Your opponent must also apply ${buffName} to their warband.`;
    }
    if (isGlobalYou) return `This will apply ${buffName} to your warband.`;
    if (isGlobalThem) return `This has no automated effect — remind your opponent to apply ${buffName} to their warband.`;
    return `This will apply the effect: ${buffName}`;
  };

  const consumeUse = (item: TreasureItem) => {
    const remaining = getItemRemainingUses(warbandId, memberId, item);
    const newRemaining = remaining - 1;
    if (newRemaining <= 0 && !item.refreshAfterGame) {
      const remainingItems = (member.treasureItems ?? [])
        .filter((t) => t.id !== item.id)
        .map((t) => t.id);
      updateMemberTreasureItems(memberId, memberRole, remainingItems);
      clearItemRemainingUses(warbandId, memberId, item.id);
    } else {
      setItemRemainingUses(warbandId, memberId, item.id, newRemaining);
    }
    setPendingUseItem(null);
  };

  const handleConfirmUse = () => {
    if (!pendingUseItem) return;
    const item = pendingUseItem;
    const buff = getApplicableBuff(item);
    if (buff) {
      const isGlobalYou = buff.globalYou > 0;
      const isGlobalThem = buff.globalThem > 0;
      if (isGlobalYou) {
        const existingWarbandIds = warbandBuffs.flatMap((b) => Array(b.stackCount ?? 1).fill(b.id));
        updateWarbandBuffs([...existingWarbandIds, buff.id]);
      }
      if (isGlobalThem) {
        setOpponentNotice(getBuffName(buff));
      }
      if (!isGlobalYou && !isGlobalThem) {
        const existingIds = (member.buffsAndDebuffs ?? []).flatMap((b) => Array(b.stackCount ?? 1).fill(b.id));
        updateMemberBuffs(memberId, memberRole, [...existingIds, buff.id]);
      }
    }
    consumeUse(item);
  };

  const handleConfirmHealUse = () => {
    if (!pendingUseItem) return;
    const healed = Math.min(maxHealth, member.currentHealth + Math.max(0, healInput));
    updateMemberHealth(memberId, memberRole, healed);
    if (pendingUseItem.healAmount === 'full' && member.poisoned) {
      toggleMemberPoisoned(memberId, memberRole);
    }
    handleConfirmUse();
  };

  const openTreasureItemPopover = (event: React.MouseEvent<HTMLElement>, item: TreasureItem) => {
    setTreasureItemPopover({ anchor: event.currentTarget, item });
  };

  const treasureItemDialogs = (
    <>
      <ItemDetailsPopover
        popover={
          treasureItemPopover
            ? {
              anchor: treasureItemPopover.anchor,
              name: treasureItemPopover.item.name,
              description: treasureItemPopover.item.description,
              tags: [],
            }
            : null
        }
        onClose={() => setTreasureItemPopover(null)}
        showUseAction={(treasureItemPopover?.item.usesPerGame ?? -1) > 0}
        useDisabled={
          treasureItemPopover !== null &&
          treasureItemPopover.item.usesPerGame > 0 &&
          getItemRemainingUses(warbandId, memberId, treasureItemPopover.item) <= 0
        }
        remainingUsesLabel={
          treasureItemPopover && treasureItemPopover.item.usesPerGame > 0
            ? `Uses remaining: ${getItemRemainingUses(warbandId, memberId, treasureItemPopover.item)} / ${treasureItemPopover.item.usesPerGame}`
            : undefined
        }
        onUse={() => {
          if (!treasureItemPopover) return;
          setPendingUseItem(treasureItemPopover.item);
          setTreasureItemPopover(null);
        }}
      />

      {/* Use item confirmation — scroll with invokedSpellId shows spell card modal */}
      {pendingUseItem?.invokedSpellId ? (
        <UseScrollModal
          open={pendingUseItem !== null}
          item={pendingUseItem}
          invokedSpellId={pendingUseItem.invokedSpellId}
          onClose={() => setPendingUseItem(null)}
          onConfirm={() => consumeUse(pendingUseItem)}
        />
      ) : pendingUseItem?.healAmount != null ? (
        <Dialog open onClose={() => setPendingUseItem(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Use {pendingUseItem.name}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {pendingUseItem.healAmount === 'full'
                ? `Restores ${member.name} to full Health${member.poisoned ? ' and cures poison' : ''}. Adjust the amount if needed.`
                : `Restores up to ${pendingUseItem.healAmount} Health. Adjust the amount if needed.`}
            </Typography>
            <TextField
              label="Health restored"
              type="number"
              value={healInput}
              onChange={(e) => setHealInput(Math.max(0, parseInt(e.target.value, 10) || 0))}
              fullWidth
              autoFocus
              slotProps={{ htmlInput: { min: 0 } }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPendingUseItem(null)}>Cancel</Button>
            <Button variant="contained" onClick={handleConfirmHealUse}>Use</Button>
          </DialogActions>
        </Dialog>
      ) : (
        <ConfirmActionDialog
          open={pendingUseItem !== null}
          title={`Use ${pendingUseItem?.name ?? 'item'}`}
          message={getUseItemMessage()}
          confirmLabel="Use"
          onClose={() => setPendingUseItem(null)}
          onConfirm={handleConfirmUse}
        />
      )}

      {/* Opponent notice after using a GlobalThem item */}
      <Snackbar
        open={opponentNotice !== null}
        onClose={() => setOpponentNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="info" onClose={() => setOpponentNotice(null)} sx={{ width: '100%' }}>
          Your opponent should apply <strong>{opponentNotice}</strong> to their warband.
        </Alert>
      </Snackbar>
    </>
  );

  return { openTreasureItemPopover, treasureItemDialogs };
};
